// DB-8 — the single, config-driven renderer for every About You "capture upstream"
// affordance. It reads a CaptureUpstreamDef (shared/constants/capture-upstream.ts)
// and, when its toggle is Yes, either renders an inline mini-list (children, pets)
// or runs a one-shot field-copy side-effect (home_address → Primary Residence).
//
// There are NO per-toggle / per-fieldKey branches here — adding a new capture is a
// config entry, not new code. All writes reuse the shipped generic_entries
// endpoints (upsert + the hard-delete row route). This mini-list deletes rows by
// hard-delete (immediate, unambiguous). NOTE (s106): the planner read path now DOES
// honour `is_hidden` (see isRowHidden in table-row-visibility) — a fully soft-hidden
// row is suppressed — but the mini-list keeps hard-delete as its removal semantics.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { GenericCategory, GenericSection, GenericField, GenericEntry } from "@shared/schema";
import type { CaptureUpstreamDef } from "@shared/constants/capture-upstream";
import { companionRowId } from "@shared/constants/capture-upstream";
import { isTypedRowSection } from "@shared/constants/typed-sections";
import { SavedElsewhereNotice } from "@/components/SavedElsewhereNotice";

interface CaptureUpstreamRendererProps {
  def: CaptureUpstreamDef;
  clientId: string;
  /** Current value of the gating About You toggle (mini-list / field-copy). */
  value: boolean;
  /** field-copy / select-field-copy: the current value of the About You source field. */
  sourceValue?: string;
  /** select-field-copy (s109 Build 4a): the current value of the gating About You select. */
  selectValue?: string;
}

export function CaptureUpstreamRenderer({ def, clientId, value, sourceValue, selectValue }: CaptureUpstreamRendererProps) {
  const { toast } = useToast();

  // s109 (Build 4a): select-field-copy resolves the branch matching the current select
  // value (undefined for "Neither" / no match → seeds nothing). The active branch's
  // target drives the resolution queries + the copy effect below; everything else
  // (mini-list / field-copy) reads the def's own target unchanged.
  const activeBranch =
    def.type === "select-field-copy"
      ? def.branches.find((b) => b.whenValue === (selectValue ?? "")) ?? null
      : null;
  // s111 (Build 5): select-mini-list resolves the branch matching the current select
  // value (no match — e.g. "Retired" — → null → renders nothing).
  const selectMiniBranch =
    def.type === "select-mini-list"
      ? def.branches.find((b) => b.whenValue === (selectValue ?? "")) ?? null
      : null;
  const targetCategory =
    def.type === "select-field-copy" ? activeBranch?.targetCategory
    : def.type === "select-mini-list" ? selectMiniBranch?.targetCategory
    : def.targetCategory;
  const targetSection =
    def.type === "select-field-copy" ? activeBranch?.targetSection
    : def.type === "select-mini-list" ? selectMiniBranch?.targetSection
    : def.targetSection;
  // Gate: mini-list / field-copy use the boolean toggle `value`; select-field-copy /
  // select-mini-list are active whenever a branch matches the current select value.
  const gateActive =
    def.type === "select-field-copy" ? !!activeBranch
    : def.type === "select-mini-list" ? !!selectMiniBranch
    : value;
  const enabledBase = gateActive && !!clientId;

  // Surface a clear, non-fatal error instead of swallowing capture-upstream write
  // failures silently (a silent no-op left the planner with no feedback).
  const notifyWriteError = (err: unknown) => {
    toast({
      title: "Couldn't save that",
      description: err instanceof Error ? err.message : "Please try again.",
      variant: "destructive",
    });
  };

  // ── resolution: category → section → fields → entries (lazy: only when toggle on) ──
  const { data: category } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", targetCategory, clientId],
    queryFn: async () =>
      (await apiRequest("GET", `/api/generic-categories/by-slug/${targetCategory}?clientId=${clientId}`)).json(),
    enabled: enabledBase && !!targetCategory,
  });
  const categoryType = category?.categoryType;

  const { data: sections = [] } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", category?.id, "sections"],
    enabled: enabledBase && !!category?.id,
  });
  const sectionId = useMemo(
    () => sections.find((s) => s.slug === targetSection)?.id,
    [sections, targetSection],
  );
  // DB-38: the target section's display name, for the field-copy success toast.
  const targetSectionName = useMemo(
    () => sections.find((s) => s.slug === targetSection)?.name,
    [sections, targetSection],
  );

  const { data: fields = [] } = useQuery<GenericField[]>({
    queryKey: ["/api/generic-sections", sectionId, "fields"],
    enabled: enabledBase && !!sectionId,
  });
  const fieldIdByKey = useMemo(() => {
    const m = new Map<string, string>();
    fields.forEach((f) => m.set(f.fieldKey, f.id));
    return m;
  }, [fields]);

  const { data: entries = [] } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", categoryType],
    queryFn: async () => {
      const r = await fetch(`/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to load entries");
      return r.json();
    },
    enabled: enabledBase && !!categoryType,
  });

  const sectionEntries = useMemo(
    () => entries.filter((e) => e.sectionId === sectionId),
    [entries, sectionId],
  );

  // D-170.6 conversions. Both are class-safe, so neither takes a lock: the entry write is the
  // (client, field, row) upsert, and the row delete targets a named rowId — a second fire is a
  // no-op. One `deleteRowMutation` serves both the row and its companion row: same endpoint,
  // same semantics, and they fire in sequence rather than concurrently.
  const upsertEntryMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, body),
  });
  const deleteRowMutation = useMutation({
    mutationFn: async (v: { rowId: string; sectionId: string }) =>
      await apiRequest("DELETE", `/api/clients/${clientId}/generic-rows/${v.rowId}?sectionId=${v.sectionId}`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries", categoryType] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientId] });
  };

  const upsert = async (fieldId: string, rowId: string, val: string) => {
    if (!category?.id || !sectionId) return;
    await upsertEntryMutation.mutateAsync({
      categoryId: category.id,
      sectionId,
      fieldId,
      rowId,
      value: val,
    });
  };

  // ── s110 (Build 4c / AY-3): companion seed (children mini-list → Government IDs) ──
  // A mini-list may declare a `companionSeed`: on each row-create it also seeds ONE plain
  // row into a SECOND section in another category (child name → a Gov IDs `person_name`
  // row). This resolves that second category→section→fields→entries chain. Only a
  // mini-list WITH a companionSeed engages it (queries stay disabled otherwise). Writes
  // reuse the same generic_entries upsert, so the companion row is a plain row
  // (is_seeded=false) — exactly like a planner typing the name into a new Gov IDs row.
  // No completion/section-status change; the row is NOT shaded (non-`__seeded__` rowId).
  const companion = def.type === "mini-list" ? def.companionSeed : undefined;
  const companionEnabled = enabledBase && !!companion;

  const { data: companionCategory } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", companion?.targetCategory, clientId],
    queryFn: async () =>
      (await apiRequest("GET", `/api/generic-categories/by-slug/${companion!.targetCategory}?clientId=${clientId}`)).json(),
    enabled: companionEnabled && !!companion?.targetCategory,
  });
  const companionCategoryType = companionCategory?.categoryType;

  const { data: companionSections = [] } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", companionCategory?.id, "sections"],
    enabled: companionEnabled && !!companionCategory?.id,
  });
  const companionSectionId = useMemo(
    () => companionSections.find((s) => s.slug === companion?.targetSection)?.id,
    [companionSections, companion?.targetSection],
  );

  const { data: companionFields = [] } = useQuery<GenericField[]>({
    queryKey: ["/api/generic-sections", companionSectionId, "fields"],
    enabled: companionEnabled && !!companionSectionId,
  });
  const companionFieldIdByKey = useMemo(() => {
    const m = new Map<string, string>();
    companionFields.forEach((f) => m.set(f.fieldKey, f.id));
    return m;
  }, [companionFields]);

  const { data: companionAllEntries = [] } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", companionCategoryType],
    queryFn: async () => {
      const r = await fetch(`/api/clients/${clientId}/generic-entries?categoryType=${companionCategoryType}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to load entries");
      return r.json();
    },
    enabled: companionEnabled && !!companionCategoryType,
  });
  const companionSectionEntries = useMemo(
    () => companionAllEntries.filter((e) => e.sectionId === companionSectionId),
    [companionAllEntries, companionSectionId],
  );

  const invalidateCompanion = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries", companionCategoryType] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientId] });
  };

  const companionUpsert = async (rowId: string, fieldId: string, val: string) => {
    if (!companionCategory?.id || !companionSectionId) return;
    await upsertEntryMutation.mutateAsync({
      categoryId: companionCategory.id,
      sectionId: companionSectionId,
      fieldId,
      rowId,
      value: val,
    });
  };

  // mini-list local state (declared unconditionally; only read in that branch)
  const [localEdits, setLocalEdits] = useState<Record<string, string>>({});
  const [pendingRowIds, setPendingRowIds] = useState<string[]>([]);

  // ── field-copy / select-field-copy: one-shot, non-destructive side-effect (no UI) ──
  // Resolve the copy target from the def (field-copy) or the active select branch
  // (select-field-copy, s109 Build 4a). `targetRowMatch` fills an existing scaffold row
  // (Own → Primary Residence); `seedRowId` writes a deterministic append row (Rent →
  // Things I Rent). onlyIfEmpty=true never overwrites a real downstream value; onlyIfEmpty=false
  // MIRRORS the source on every change (D-154.24, the home_tenure="Own" → Primary Residence case).
  const copyCfg =
    def.type === "field-copy"
      ? { targetField: def.targetField, targetRowMatch: def.targetRowMatch, seedRowId: undefined as string | undefined, onlyIfEmpty: def.onlyIfEmpty }
      : def.type === "select-field-copy" && activeBranch
        ? { targetField: activeBranch.targetField, targetRowMatch: activeBranch.targetRowMatch, seedRowId: activeBranch.seedRowId, onlyIfEmpty: activeBranch.onlyIfEmpty }
        : null;

  const copiedRef = useRef(false);
  // Re-arm when the gating select value changes so the NEW branch seeds (seed-and-leave:
  // the prior branch's row is never removed — the planner deletes what doesn't apply).
  const lastSelectRef = useRef<string | undefined>(undefined);
  // D-154.20 A: ALSO re-arm when the source field changes (e.g. home_address edited while
  // home_tenure is already "Own"), so the copy fires regardless of the order the planner answered in.
  // D-154.24: for the "Own" branch (onlyIfEmpty=false) this re-arm is what turns the copy into a
  // one-way MIRROR — each home_address edit re-syncs the Primary Residence address. For onlyIfEmpty
  // branches the re-arm only ever fills an empty target, so it stays non-destructive.
  const lastSourceRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (def.type === "select-field-copy" && lastSelectRef.current !== selectValue) {
      lastSelectRef.current = selectValue;
      copiedRef.current = false;
    }
    if (def.type === "select-field-copy" && lastSourceRef.current !== sourceValue) {
      lastSourceRef.current = sourceValue;
      copiedRef.current = false;
    }
    if (!gateActive) { copiedRef.current = false; return; }
    if (!copyCfg) return;
    if (!enabledBase || !sectionId || !category?.id || copiedRef.current) return;
    const src = (sourceValue ?? "").trim();
    if (!src) return;
    const targetFieldId = fieldIdByKey.get(copyCfg.targetField);
    if (!targetFieldId) return;
    // Destination rowId: match an existing scaffold row, or a deterministic seed row.
    let targetRowId: string | undefined;
    // s230 PhysicalAsset (1): in a TYPED section a seeded scaffold row is not a record and never shows, so
    // when no row matches, the copy makes one — a new record carrying the match value, then the copied field.
    // s234 (CH2-STORAGE-PR3): the match is the FIRST entry; a new record carries every entry (the home flag and its kind).
    let createWith: { fieldId: string; value: string }[] | undefined;
    if (copyCfg.targetRowMatch) {
      const [matchKey, matchVal] = Object.entries(copyCfg.targetRowMatch)[0];
      const matchFieldId = fieldIdByKey.get(matchKey);
      if (!matchFieldId) return;
      targetRowId = sectionEntries.find(
        (e) => e.fieldId === matchFieldId && (e.value ?? "").trim() === matchVal,
      )?.rowId ?? undefined;
      if (!targetRowId && isTypedRowSection(targetSection)) {
        targetRowId = crypto.randomUUID();
        createWith = Object.entries(copyCfg.targetRowMatch)
          .map(([k, v]) => ({ fieldId: fieldIdByKey.get(k) ?? "", value: v }))
          .filter((c) => c.fieldId !== "");
      }
    } else if (copyCfg.seedRowId) {
      targetRowId = copyCfg.seedRowId;
    }
    if (!targetRowId) return;
    const targetEntry = sectionEntries.find(
      (e) => e.rowId === targetRowId && e.fieldId === targetFieldId,
    );
    if (copyCfg.onlyIfEmpty && (targetEntry?.value ?? "").trim()) return; // never overwrite
    copiedRef.current = true;
    const rowId = targetRowId;
    (createWith ?? []).reduce((p, c) => p.then(() => upsert(c.fieldId, rowId, c.value)), Promise.resolve() as Promise<unknown>)
      .then(() => upsert(targetFieldId, rowId, src))
      .then(() => {
        invalidate();
        // s155 (D-155.4): the ephemeral "Copied to …" toast is RETIRED — the copy is now
        // confirmed by the persistent source-side SavedElsewhereNotice rendered below, so
        // the notice never disappears and never double-announces (see the Rent flow).
      })
      .catch((err) => { copiedRef.current = false; notifyWriteError(err); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, value, selectValue, gateActive, enabledBase, sectionId, category?.id, sourceValue, sectionEntries, fieldIdByKey]);

  // field-copy / select-field-copy are pure side-effects (no mini-list UI), but s155
  // (D-155.4) gives them the ONE standard persistent, source-side notice — replacing the
  // retired toast (flow 7 home→Properties) and collapsing the flow-8 Rent double-notice
  // (its bespoke GenericCategoryPage pointer is removed). Shown once a branch is active and
  // the destination section name has resolved.
  if (def.type === "field-copy" || def.type === "select-field-copy") {
    if (gateActive && targetSectionName) {
      return <SavedElsewhereNotice destination={targetSectionName} testId={`saved-elsewhere-${def.type}`} />;
    }
    return null;
  }

  // Resolve the mini-list config from the def (mini-list) or the active branch
  // (select-mini-list, s111). `testKey` namespaces the data-testids. companionSeed is
  // mini-list-only (handled by the `companion` var above), so select-mini-list has none.
  const mini =
    def.type === "mini-list"
      ? { fields: def.fields, addLabel: def.addLabel, recordNoun: def.recordNoun, testKey: def.toggleField }
      : def.type === "select-mini-list" && selectMiniBranch
        ? { fields: selectMiniBranch.fields, addLabel: selectMiniBranch.addLabel, recordNoun: selectMiniBranch.recordNoun, testKey: def.selectField }
        : null;
  // mini-list gates on the boolean toggle; select-mini-list gates on a matching branch.
  if (def.type === "mini-list" && !value) return null;
  if (!mini) return null;

  // ── mini-list render ──
  const firstKey = mini.fields[0].key;

  // s109 (Build 4b): "address" capture fields (A-lite). The mini-list keeps a single
  // compact street input; on write we wrap it into the same structured address JSON the
  // target `address` field uses (street filled, the rest blank for the planner to refine
  // in the section). On read we unwrap back to the street for display.
  const isAddressField = (fieldKey: string): boolean =>
    mini.fields.find((f) => f.key === fieldKey)?.fieldType === "address";
  const addressStreet = (raw: string): string => {
    if (!raw) return "";
    try {
      const o = JSON.parse(raw);
      return o && typeof o.street === "string" ? o.street : raw;
    } catch {
      return raw; // plain-text value (e.g. an in-progress edit) — show as-is
    }
  };
  const wrapAddress = (text: string): string => {
    const t = text.trim();
    return t ? JSON.stringify({ street: t, street2: "", city: "", state: "", postal: "", country: "" }) : "";
  };
  /** Street text to show in the input (unwrapped for address fields). */
  const displayValue = (rowId: string, fieldKey: string): string => {
    const raw = valueOf(rowId, fieldKey);
    return isAddressField(fieldKey) ? addressStreet(raw) : raw;
  };

  const valueOf = (rowId: string, fieldKey: string): string => {
    const editKey = `${rowId}:${fieldKey}`;
    if (localEdits[editKey] !== undefined) return localEdits[editKey];
    const fid = fieldIdByKey.get(fieldKey);
    return sectionEntries.find((e) => e.rowId === rowId && e.fieldId === fid)?.value ?? "";
  };

  // existing rows = section rows whose first capture field is filled (skips blank
  // scaffolds); newly-added pending rows append below them.
  const rowOrder: string[] = [];
  const seen = new Set<string>();
  for (const e of sectionEntries) {
    if (e.rowId && !seen.has(e.rowId)) { seen.add(e.rowId); rowOrder.push(e.rowId); }
  }
  const existingRows = rowOrder.filter(
    (rid) => !pendingRowIds.includes(rid) && valueOf(rid, firstKey).trim() !== "",
  );
  const displayRows = [...existingRows, ...pendingRowIds];

  const handleBlur = async (rowId: string, fieldKey: string) => {
    const fid = fieldIdByKey.get(fieldKey);
    if (!fid) return;
    // s109 (Build 4b): wrap an edited address into structured JSON. Only when the planner
    // actually edited it — an untouched row writes its stored value back verbatim, so we
    // never re-wrap (and clobber) a full address the planner refined in the section.
    const editKey = `${rowId}:${fieldKey}`;
    const edited = localEdits[editKey] !== undefined;
    const toStore =
      isAddressField(fieldKey) && edited ? wrapAddress(localEdits[editKey]) : valueOf(rowId, fieldKey);
    try {
      await upsert(fid, rowId, toStore);
      if (pendingRowIds.includes(rowId) && fieldKey === firstKey && displayValue(rowId, firstKey).trim()) {
        setPendingRowIds((prev) => prev.filter((r) => r !== rowId));
      }
      invalidate();

      // s110 (Build 4c / AY-3): mirror the mini-list identifier (first field, e.g. child
      // name) into the companion section as a plain row — create on add, re-sync on rename.
      // Fire only when the identifier was actually edited AND its value differs from what's
      // already in the companion row (no redundant writes → "fire only when it changed").
      // Idempotent: the companion rowId is deterministic (`__childgovid__<childRowId>`), so
      // editing the same child never duplicates the Gov IDs row.
      // BENEFICIARY-FILL-1 (s156): the mirrored value is the join of `companionSourceKeys`
      // (e.g. first_name + surname → a full name); absent → the single first field. Editing
      // any source key re-syncs.
      const companionSourceKeys = companion?.companionSourceKeys ?? [firstKey];
      if (companion && companionSourceKeys.includes(fieldKey) && edited && companionSectionId) {
        const name = companionSourceKeys.map((k) => valueOf(rowId, k).trim()).filter(Boolean).join(" ").trim();
        const targetFieldId = companionFieldIdByKey.get(companion.targetField);
        if (name && targetFieldId) {
          const compRowId = companionRowId(companion.rowIdPrefix, rowId);
          const current = companionSectionEntries.find(
            (e) => e.rowId === compRowId && e.fieldId === targetFieldId,
          );
          if ((current?.value ?? "").trim() !== name) {
            await companionUpsert(compRowId, targetFieldId, name);
            invalidateCompanion();
          }
        }
      }
    } catch (err) {
      notifyWriteError(err);
    }
  };

  const handleRemove = async (rowId: string) => {
    const wasPending = pendingRowIds.includes(rowId);
    setPendingRowIds((prev) => prev.filter((r) => r !== rowId));
    setLocalEdits((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) if (k.startsWith(`${rowId}:`)) delete next[k];
      return next;
    });
    if (!wasPending && sectionId) {
      try {
        await deleteRowMutation.mutateAsync({ rowId, sectionId });
        invalidate();
      } catch (err) {
        notifyWriteError(err);
      }

      // s110 (Build 4c / AY-3): if the removed row had a companion Gov IDs row and it's
      // still pristine (ONLY the identifier `person_name` filled — nothing the planner
      // added), remove it too, matching the child's own hard-delete. If the planner filled
      // any other detail (e.g. id_type), LEAVE it. Cat A id_number never reaches the
      // server, so pristine is judged on server-visible fields only (matches "behave
      // exactly like a manual name-only row"). Only persisted rows have a companion.
      if (companion && companionSectionId) {
        const compRowId = companionRowId(companion.rowIdPrefix, rowId);
        const targetFieldId = companionFieldIdByKey.get(companion.targetField);
        const rowEntries = companionSectionEntries.filter((e) => e.rowId === compRowId);
        const pristine =
          rowEntries.length > 0 &&
          rowEntries.every((e) => e.fieldId === targetFieldId || (e.value ?? "").trim() === "");
        if (pristine) {
          try {
            await deleteRowMutation.mutateAsync({ rowId: compRowId, sectionId: companionSectionId });
            invalidateCompanion();
          } catch (err) {
            notifyWriteError(err);
          }
        }
      }
    }
  };

  return (
    <div className="mt-3.5 rounded-md border border-sage-100 bg-sage-50 p-3" data-testid={`capture-${mini.testKey}`}>
      <div className="flex flex-col gap-2">
        {displayRows.length === 0 && (
          <p className="text-xs text-warm-taupe">Add each {mini.recordNoun} so the details carry through.</p>
        )}
        {displayRows.map((rowId) => (
          <div key={rowId} className="flex items-end gap-2">
            {mini.fields.map((f) => (
              <label key={f.key} className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[11px] text-warm-taupe">{f.label}</span>
                {f.fieldType === "select" ? (
                  <select
                    className="rounded border border-warm-stone bg-warm-white px-2 py-1 text-sm"
                    value={valueOf(rowId, f.key)}
                    onChange={(e) => setLocalEdits((p) => ({ ...p, [`${rowId}:${f.key}`]: e.target.value }))}
                    onBlur={() => handleBlur(rowId, f.key)}
                    data-testid={`capture-${mini.testKey}-${f.key}`}
                  >
                    <option value="">Select…</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.fieldType === "date" ? "date" : "text"}
                    className="rounded border border-warm-stone bg-warm-white px-2 py-1 text-sm"
                    // s171: a declared placeholder wins; the address default stays the fallback.
                    placeholder={f.placeholder ?? (f.fieldType === "address" ? "Street address" : undefined)}
                    value={displayValue(rowId, f.key)}
                    onChange={(e) => setLocalEdits((p) => ({ ...p, [`${rowId}:${f.key}`]: e.target.value }))}
                    onBlur={() => handleBlur(rowId, f.key)}
                    data-testid={`capture-${mini.testKey}-${f.key}`}
                  />
                )}
              </label>
            ))}
            <button
              type="button"
              onClick={() => handleRemove(rowId)}
              className="mb-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label={`Remove ${mini.recordNoun}`}
              data-testid={`capture-remove-${mini.testKey}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setPendingRowIds((prev) => [...prev, crypto.randomUUID()])}
          className="flex items-center gap-1 self-start text-[13px] font-medium text-[#4F6B51] hover:underline"
          data-testid={`capture-add-${mini.testKey}`}
        >
          <Plus className="h-3.5 w-3.5" /> {mini.addLabel.replace(/^\+\s*/, "")}
        </button>
        {/* s155 (D-155.4): the ONE standard source-side notice — the mini-list records also
            live in their destination section (flows 1/3/4/5/6). Replaces the silent copy /
            the bare empty-state prompt as the durable "where did this go" signal. */}
        <SavedElsewhereNotice destination={targetSectionName} testId={`saved-elsewhere-${mini.testKey}`} />
      </div>
    </div>
  );
}
